/**
 * Restaurant startup checklist — derived from the app's process map and opening plan.
 * Used for a free public checklist to help founders track progress from idea to open.
 * Items link to app sections via tabId where applicable.
 */
export const STARTUP_CHECKLIST_PHASES = [
  {
    id: 'preparation',
    title: 'Phase 1: Preparation & Planning',
    description: 'Define your concept, pitch, and team structure',
    duration: '2–4 weeks',
    icon: 'Lightbulb',
    color: 'blue',
    tabId: 'concept-pitch',
    items: [
      { id: 'concept-defined', label: 'Restaurant concept and vision defined', tabId: 'concept-pitch' },
      { id: 'elevator-pitch', label: 'Elevator pitch written', tabId: 'concept-pitch' },
      { id: 'exec-summary', label: 'Executive summary drafted', tabId: 'concept-pitch' },
      { id: 'team-plan', label: 'Management team structure outlined', tabId: 'team-cap-table' },
      { id: 'market-research', label: 'Market and competition researched', tabId: 'market-competition' },
    ],
  },
  {
    id: 'financial',
    title: 'Phase 2: Financial Planning & Funding',
    description: 'Startup costs, funding strategy, and lender package',
    duration: '3–6 weeks',
    icon: 'DollarSign',
    color: 'green',
    tabId: 'financials',
    items: [
      { id: 'startup-costs', label: 'Startup costs calculated (buildout, equipment, permits)', tabId: 'financials' },
      { id: 'funding-strategy', label: 'Funding strategy identified (SBA, investors, personal)', tabId: 'financials' },
      { id: 'projections', label: '3-year financial projections created', tabId: 'financials' },
      { id: 'lender-package', label: 'Lender/investor package assembled', tabId: 'financials' },
      { id: 'ein-entity', label: 'EIN obtained and business entity registered', tabId: 'compliance' },
      { id: 'business-bank', label: 'Business bank account opened', tabId: 'financials' },
    ],
  },
  {
    id: 'location',
    title: 'Phase 3: Location & Real Estate',
    description: 'Neighborhood research, property search, lease, and zoning',
    duration: '4–8 weeks',
    icon: 'MapPin',
    color: 'purple',
    tabId: 'operations',
    items: [
      { id: 'neighborhood-research', label: 'Target neighborhoods researched', tabId: 'operations' },
      { id: 'property-search', label: 'Properties viewed and shortlisted', tabId: 'operations' },
      { id: 'lease-negotiated', label: 'Lease negotiated and signed', tabId: 'operations' },
      { id: 'zoning-verified', label: 'Zoning verified for restaurant use (and alcohol if applicable)', tabId: 'compliance' },
      { id: 'design-locked', label: 'Design and layout plans locked', tabId: 'equipment-menu' },
    ],
  },
  {
    id: 'permits',
    title: 'Phase 4: Permits & Compliance',
    description: 'Business cert, building permits, food permit, alcohol (if applicable), inspections',
    duration: '2–4 months',
    icon: 'Shield',
    color: 'red',
    tabId: 'compliance',
    items: [
      { id: 'business-certificate', label: 'Boston Business Certificate obtained', tabId: 'compliance' },
      { id: 'building-permits', label: 'Building permits applied for / obtained', tabId: 'compliance' },
      { id: 'food-manager-cert', label: 'Certified Food Protection Manager (e.g. ServSafe)', tabId: 'compliance' },
      { id: 'food-establishment-permit', label: 'Food Establishment Permit application submitted', tabId: 'compliance' },
      { id: 'alcohol-license', label: 'Alcohol license applied for (if applicable)', tabId: 'compliance' },
      { id: 'co-obtained', label: 'Certificate of Occupancy obtained', tabId: 'compliance' },
      { id: 'inspections-passed', label: 'Health and fire inspections passed', tabId: 'compliance' },
    ],
  },
  {
    id: 'buildout',
    title: 'Phase 5: Buildout & Equipment',
    description: 'Design, construction, equipment selection and install',
    duration: '2–4 months',
    icon: 'Wrench',
    color: 'orange',
    tabId: 'equipment-menu',
    items: [
      { id: 'contractor-hired', label: 'General contractor and subs engaged', tabId: 'equipment-menu' },
      { id: 'equipment-ordered', label: 'Equipment ordered (long-lead items first)', tabId: 'equipment-menu' },
      { id: 'buildout-complete', label: 'Buildout and construction complete', tabId: 'equipment-menu' },
      { id: 'equipment-installed', label: 'Equipment installed and operational', tabId: 'equipment-menu' },
      { id: 'hood-grease-trap', label: 'Hood, fire suppression, and grease trap compliant', tabId: 'compliance' },
    ],
  },
  {
    id: 'operations',
    title: 'Phase 6: Operations & Launch',
    description: 'Staffing, training, POS, inventory, soft open, grand opening',
    duration: '1–2 months',
    icon: 'Rocket',
    color: 'indigo',
    tabId: 'startup-and-opening',
    items: [
      { id: 'key-hires', label: 'Key hires in place (GM, chef, key FOH/BOH)', tabId: 'team-cap-table' },
      { id: 'pos-payments', label: 'POS and payments tested and live', tabId: 'financials' },
      { id: 'opening-inventory', label: 'Opening inventory ordered and scheduled', tabId: 'vendors-expenses' },
      { id: 'insurance-filed', label: 'Insurance certificates filed (landlord, lender)', tabId: 'compliance' },
      { id: 'training-week', label: 'Training week completed (food safety, POS, service)', tabId: 'team-cap-table' },
      { id: 'first-week-schedule', label: 'First-week staffing schedule published', tabId: 'team-cap-table' },
      { id: 'soft-opening', label: 'Soft opening completed', tabId: 'startup-and-opening' },
      { id: 'grand-opening', label: 'Grand opening day run-of-show ready', tabId: 'startup-and-opening' },
    ],
  },
];
